import { useQuery } from '@tanstack/react-query';
import { linkOptions } from '@tanstack/react-router';

import { ApprovalRequestState } from '../../../types/ApprovalRequestState';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { queries } from '../../common/api/queryKeys/queries';
import { GenericError } from '../../common/Error/GenericError';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { formatTotalPrice } from '../../common/helpers/formatTotal.helper';
import { formatVessel } from '../../common/helpers/formatVessel.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { useRole } from '../../common/hooks/useRole';
import { useFlags } from '../../common/launchDarkly/useFlags';
import { Section } from '../../common/Section/Section';
import { OfferModel } from '../../offer/model/OfferModel';
import { Port } from '../../port/Port';

export const ApprovalRequestTable = () => {
    const { showApprovalRequestsInTool } = useFlags();
    const role = useRole();
    const userEmailAddress = role.user.email;

    const approvalRequestsQuery = useQuery({
        ...queries.approvalRequests.byEmailAndState(userEmailAddress, ApprovalRequestState.PENDING),
        enabled: Boolean(!!userEmailAddress && showApprovalRequestsInTool),
    });

    if (!showApprovalRequestsInTool || approvalRequestsQuery.isPending) {
        return null;
    }

    if (approvalRequestsQuery.isError) {
        return (
            <Section title={translate('dashboard.approvalRequests')}>
                <GenericError />
            </Section>
        );
    }

    if (!approvalRequestsQuery.data.approvalRequests.length) {
        return null;
    }

    const buildRowLink = (offer: OfferModel) => {
        return linkOptions({ to: '/offer/$offerId/approval-request', params: { offerId: offer.id } });
    };

    return (
        <Section title={translate('dashboard.approvalRequests')}>
            <TableBuilder
                data={approvalRequestsQuery.data.offers}
                rowLink={(offer) => buildRowLink(offer)}
                grow={false}
            >
                <TableBuilderColumn<OfferModel> header={translate('order.buyerreferenceshort')} width="10%" truncate>
                    {(offer) => offer.buyerReference || Characters.EN_DASH}
                </TableBuilderColumn>

                {!role.isOneCompanyUser() && (
                    <TableBuilderColumn<OfferModel>
                        header={role.isCustomer() ? translate('order.company') : translate('order.customer')}
                        width="10%"
                        truncate
                    >
                        {(offer) => formatCompanyName({ company: offer.customer })}
                    </TableBuilderColumn>
                )}

                <TableBuilderColumn<OfferModel> header={translate('order.vessel')} width="15%" truncate>
                    {(offer) => formatVessel({ vessel: offer.vessel, short: true })}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.port')} width="15%">
                    {(offer) => {
                        if (!offer.port) {
                            return null;
                        }
                        return <Port port={offer.port} vesselId={offer.vesselId} locode={false} showTooltip />;
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.supplier')} width="15%" truncate>
                    {(offer) => formatCompanyName({ company: offer.supplier })}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('order.dateDeliveryShort')} width="10%">
                    {(offer) => {
                        if (!offer.dateDelivery) {
                            return null;
                        }
                        return formatDate({ date: offer.dateDelivery, timeZone: 'UTC' });
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('dashboard.approvalRequestsCreatedBy')}>
                    {(offer) => {
                        return [
                            offer.approvalRequests[0].createdByUser.firstname,
                            offer.approvalRequests[0].createdByUser.lastname,
                        ]
                            .filter(Boolean)
                            .join(' ');
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('dashboard.approvalRequestsCreatedAt')}>
                    {(offer) => formatDate({ date: offer.approvalRequests[0].dateCreated })}
                </TableBuilderColumn>

                <TableBuilderColumn<OfferModel> header={translate('dashboard.approvalRequestsTotal')}>
                    {(offer) => {
                        if (!offer.total) {
                            return null;
                        }
                        return formatTotalPrice(offer.total);
                    }}
                </TableBuilderColumn>
            </TableBuilder>
        </Section>
    );
};
